// The notification switches as the Settings page groups and labels them. The web settings panel
// renders these and the admin Recipients tab reads them, so the two surfaces cannot drift.
//
// A group's `id` is its name in anything that has to point at it later, such as a Registry proposal
// placing a new group after an existing one. Titles are display text and two of them already collide
// across audiences, so an id is unique across both lists and never changes once a group ships.
//
// Option flags: `webOnly` (email-only lanes), `webOnlyComingToApp` (stored push,email but rendered
// as email until the app catches up), `pushOnly` (stored push,email so the default reads as on and
// the lane's one email still sends), `isRecommended` (warn when turned off). `creatorOnly` groups
// are hidden from shoppers. An option with `choices` is a style picker rather than a channel switch;
// `requiresFeatureFlag` hides it until the user has the flag and `lockedWhenSettingIsNone` disables
// it while the named switch is off; `choiceLabel` is what the chosen value is called under it.
const { GIFTING_ALERT_STYLES, DEFAULT_GIFTING_ALERT_STYLE } = require('./gifting_alert_styles');

const USER_NOTIFICATION_SETTING_GROUPS = [
  {
    id: 'messages_from_brands',
    title: 'Messages from Brands',
    creatorOnly: true,
    options: [
      {
        display: 'General Messages',
        subdisplay: 'Wider updates or announcements from brands.',
        setting: 'notifyOnGeneralMessages'
      },
      {
        display: 'Direct Messages',
        subdisplay: 'Personalized messages sent directly to you from brands.',
        isRecommended: true,
        setting: 'notifyOnDirectMessages'
      }
    ]
  },
  {
    id: 'gifting_requests',
    title: 'Gifting Requests',
    creatorOnly: true,
    options: [
      {
        display: 'New Gifting Requests',
        subdisplay: 'Get notified when a brand sends you a gifting request.',
        isRecommended: true,
        setting: 'notifyOnNewGiftingRequests'
      },
      {
        display: 'Updates to Gifting Requests',
        subdisplay: 'Get notified about changes to gifting requests such as delivery status.',
        setting: 'notifyOnUpdatedGiftingRequests'
      },
      {
        display: 'Gifting Alerts',
        subdisplay: 'How new gifting requests reach you. Roundups arrive at 6pm ET.',
        requiresFeatureFlag: 'newGiftingOffersTab',
        lockedWhenSettingIsNone: 'notifyOnNewGiftingRequests',
        choices: GIFTING_ALERT_STYLES,
        choiceLabel: 'New requests',
        defaultValue: DEFAULT_GIFTING_ALERT_STYLE,
        setting: 'giftingAlertStyle'
      }
    ]
  },
  {
    id: 'opportunities',
    title: 'Opportunities',
    creatorOnly: true,
    options: [
      {
        display: 'New Opportunities',
        subdisplay: 'Get notified when a brand sends a new opportunity.',
        isRecommended: true,
        setting: 'notifyOnNewOpportunities'
      },
      {
        display: 'Updates to Opportunities',
        subdisplay: 'Get notified when you complete your opportunity and payment has been transferred.',
        setting: 'notifyOnUpdatedOpportunities'
      }
    ]
  },
  {
    id: 'discount_codes',
    title: 'Discount Codes',
    creatorOnly: true,
    options: [
      {
        display: 'New Discount Codes',
        subdisplay: 'Get notifications when a brand has sent you a new discount code.',
        isRecommended: true,
        setting: 'notifyOnNewDiscountCodes'
      },
      {
        display: 'Updates to Discount Codes',
        subdisplay: 'Get notifications when a brand has disabled your discount code.',
        isRecommended: true,
        setting: 'notifyOnUpdatedDiscountCodes'
      }
    ]
  },
  {
    id: 'sales',
    title: 'Sales',
    creatorOnly: true,
    options: [
      {
        display: 'Saved Sale Reminders',
        subdisplay: 'A morning roundup of sales you asked to be reminded about, on the day they start.',
        setting: 'notifyOnSalesReminders'
      }
    ]
  },
  {
    id: 'spotlights',
    title: 'Spotlights',
    creatorOnly: true,
    options: [
      {
        display: 'Spotlight Requests',
        subdisplay: 'Get notified when a brand wants to spotlight your content, and before a request expires.',
        isRecommended: true,
        pushOnly: true,
        setting: 'notifyOnSpotlights'
      }
    ]
  },
  {
    id: 'earnings',
    title: 'Earnings',
    creatorOnly: true,
    options: [
      {
        display: 'Payments',
        subdisplay: 'Get a notification the moment a payment is sent to your PayPal or Stripe account.',
        pushOnly: true,
        setting: 'notifyOnPayouts'
      },
      {
        display: 'Orders',
        subdisplay: 'Get notified about daily commission milestones.',
        setting: 'notifyOnOrders'
      },
      {
        display: 'Referrals',
        subdisplay: 'Get notified about your referrals, such as when someone joins or reaches a new tier.',
        setting: 'notifyOnReferrals'
      }
    ]
  },
  {
    id: 'account',
    title: 'Account',
    creatorOnly: true,
    options: [
      {
        display: 'Account Updates',
        subdisplay: 'Get notified on key account updates, such as creator tier changes or social account disconnection.',
        isRecommended: true,
        setting: 'notifyOnAccountUpdates'
      }
    ]
  },
  {
    id: 'performance_updates',
    title: 'Performance Updates',
    creatorOnly: true,
    options: [
      {
        display: 'Link Activity',
        subdisplay: 'Get notified when your links start getting clicks and reach milestones.',
        setting: 'notifyOnLinkActivity'
      },
      {
        display: 'Earnings Milestones',
        subdisplay: 'Get notified when you have your highest earning day, week, or month so far.',
        setting: 'notifyOnEarningsMilestones'
      },
      {
        display: 'Weekly Performance Updates',
        subdisplay: 'Get weekly updates about your performance and updates for the week ahead.',
        isRecommended: true,
        webOnly: true,
        setting: 'notifyWeeklyPerformanceUpdates'
      }
    ]
  },
  {
    id: 'shopper_settings',
    title: 'Shopper Settings',
    options: [
      {
        display: 'Daily Moments',
        subdisplay: 'A daily Moment selected for your taste.',
        pushOnly: true,
        shopperOnly: true,
        setting: 'notifyDailyMomentShops'
      },
      {
        display: 'Weekly Roundups',
        subdisplay: 'Get weekly updates on new products added by curators in your circles.',
        webOnlyComingToApp: true,
        setting: 'notifyWeeklyCircleRoundup'
      }
    ]
  }
];

// Brand-side gates are email-only (brands receive no push or SMS); one toggle per brand surface,
// each mapping to a userNotificationSetting on the relevant email templates.
const BRAND_NOTIFICATION_SETTING_GROUPS = [
  {
    id: 'brand_messages',
    title: 'Messages',
    options: [
      {
        display: 'Messages',
        subdisplay: 'When a creator messages you or sends a partnership request.',
        webOnly: true,
        setting: 'notifyOnBrandMessages'
      }
    ]
  },
  {
    id: 'brand_gifting',
    title: 'Gifting',
    options: [
      {
        display: 'Gifting',
        subdisplay: 'When a creator accepts a gifting request.',
        webOnly: true,
        setting: 'notifyOnBrandGifting'
      }
    ]
  },
  {
    id: 'brand_collaborations',
    title: 'Collaborations',
    options: [
      {
        display: 'Collaborations',
        subdisplay: 'When a creator accepts a collaboration or updates a deliverable, or a collaboration is ready to be marked completed.',
        webOnly: true,
        setting: 'notifyOnBrandCollaborations'
      }
    ]
  },
  {
    id: 'brand_opportunities',
    title: 'Opportunities',
    options: [
      {
        display: 'Opportunities',
        subdisplay: 'When a creator accepts one of your opportunities.',
        webOnly: true,
        setting: 'notifyOnBrandOpportunities'
      }
    ]
  }
];

// The groups a given account sees on its Settings page.
const getNotificationSettingGroups = ({ isBrand, isShopper }) => {
  if (isBrand) return BRAND_NOTIFICATION_SETTING_GROUPS;
  return isShopper ? USER_NOTIFICATION_SETTING_GROUPS.filter(group => !group.creatorOnly) : USER_NOTIFICATION_SETTING_GROUPS;
};

const settingGroupsForAudiences = audiences =>
  getNotificationSettingGroups({
    isBrand: audiences.includes('brand'),
    isShopper: audiences.includes('shopper')
  });

const settingAllowsChannel = (value, channel) => typeof value === 'string' && value.split(',').includes(channel);

// The channels a switch offers a person, which is not always what the column stores: a pushOnly lane
// keeps its one email but only the push is hers to turn off, and a webOnly lane has no push to offer.
const channelsOfferedBy = option => {
  if (option.pushOnly) return ['push'];
  if (option.webOnly || option.webOnlyComingToApp) return ['email'];
  return ['push', 'email'];
};

const storedValuesOfferedBy = option => {
  if (option.webOnly) return { onValue: 'email', offValue: 'none' };
  if (option.pushOnly || option.webOnlyComingToApp) return { onValue: 'push,email', offValue: 'none' };
  return {};
};

const resolveNotificationSettingGroups = ({ isBrand, isShopper, featureFlags = {}, settings = {} }) =>
  getNotificationSettingGroups({ isBrand, isShopper })
    .map(group => ({
      ...group,
      options: group.options
        .filter(option => (!option.shopperOnly || isShopper) && (!option.requiresFeatureFlag || !!featureFlags[option.requiresFeatureFlag]))
        .map(option => ({
          ...option,
          channels: channelsOfferedBy(option),
          ...storedValuesOfferedBy(option),
          isLocked: !!option.lockedWhenSettingIsNone && settings[option.lockedWhenSettingIsNone] === 'none'
        }))
    }))
    .filter(group => group.options.length);

// Every group anyone can be shown, creator and brand alike.
const ALL_NOTIFICATION_SETTING_GROUPS = [...USER_NOTIFICATION_SETTING_GROUPS, ...BRAND_NOTIFICATION_SETTING_GROUPS];

const settingGroupById = id => ALL_NOTIFICATION_SETTING_GROUPS.find(group => group.id === id) || null;

const settingGroupIdForTitle = title =>
  String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// A title can slug to a group another audience already ships, and that id cannot be created again.
const unusedSettingGroupIdForTitle = title => {
  const base = settingGroupIdForTitle(title) || 'new_group';
  if (!settingGroupById(base)) return base;
  let suffix = 2;
  while (settingGroupById(`${base}_${suffix}`)) suffix += 1;
  return `${base}_${suffix}`;
};

// Every switch that exists today. A proposal for one that is already here is a duplicate, not a new switch.
const existingSettingNames = () => ALL_NOTIFICATION_SETTING_GROUPS.flatMap(group => group.options.map(option => option.setting));

// How the Settings page should draw a switch, from the channels its notifications actually use. The
// `webOnlyComingToApp` flag is not derivable: it says the app will catch up, which only a person knows.
const rendererForChannels = (channels = []) => {
  const hasPush = channels.includes('push');
  const hasEmail = channels.includes('email');
  if (hasPush && !hasEmail) return 'pushOnly';
  if (hasEmail && !hasPush) return 'webOnly';
  return 'normal';
};

module.exports = {
  USER_NOTIFICATION_SETTING_GROUPS,
  BRAND_NOTIFICATION_SETTING_GROUPS,
  ALL_NOTIFICATION_SETTING_GROUPS,
  getNotificationSettingGroups,
  resolveNotificationSettingGroups,
  settingGroupsForAudiences,
  settingGroupById,
  settingGroupIdForTitle,
  unusedSettingGroupIdForTitle,
  existingSettingNames,
  rendererForChannels,
  settingAllowsChannel,
  channelsOfferedBy
};
